import type { ApprovalStatus, AssetCard } from '@/lib/api/types';
import { Menu, type MenuItem } from '@/components/Menu';
import { APPROVAL_TONE } from '@/components/Misc';
import { useSelection } from '@/stores/selection';
import { usePermission } from '@/features/workspace/usePermission';
import type { AssetActionKind, AssetActionOpts } from './useAssetActions';
import { useDownloadPresets } from '@/lib/api/queries'; // release3/C
import { downloadMenuItems } from '@/features/export/DownloadMenu'; // release3/C
import { usePins } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { isPinned } from '@/features/pins/pinsModel';

export function GridContextMenu({ at, card, boardId, onClose, run }: { at: { x: number; y: number }; card: AssetCard; boardId: string | null; onClose: () => void; run: (kind: AssetActionKind, ids: string[], opts?: AssetActionOpts) => void }) {
  const ids = [...useSelection.getState().ids].filter((k) => !k.startsWith('cu_'));
  const sel = ids.length ? ids : [card.id];
  const canEdit = usePermission('EDIT_METADATA', { boardId });
  const canApprove = usePermission('APPROVE', { boardId });
  const canDownload = usePermission('DOWNLOAD', { boardId });
  const canManage = usePermission('MANAGE_BOARD', { boardId });
  const canShare = usePermission('SHARE', { boardId });
  const n = sel.length;
  const tidyItems = useTidyMenuItems(sel, card, run); // release1/B
  const downloadItems = useDownloadItems(sel, card, run, canDownload); // release3/C
  const items: MenuItem[] = [
    { label: 'Open', shortcut: 'Enter', onSelect: () => run('open', [card.id], { card }) },
    ...downloadItems, // release3/C: original + presets + custom export (single image); zip for several
    ...(canShare && n === 1 ? [{ label: 'Share…', onSelect: () => run('share', sel) }] : []),
    ...(n === 1 ? [{ label: 'Find similar', onSelect: () => run('similar', [card.id]) }] : []), // release3/B
    { kind: 'separator' as const },
    ...(canManage ? [{ label: 'Move to board…', onSelect: () => run('move', sel) }, { label: 'Copy to board…', onSelect: () => run('copy', sel) }] : []),
    ...(canEdit ? [{ label: 'Tags…', onSelect: () => run('tag', sel) }, { label: 'Set field…', onSelect: () => run('field', sel) }] : []),
    ...(canEdit || canApprove
      ? [
          { kind: 'label' as const, label: 'Approval' },
          ...(['in_progress', 'needs_review', 'approved', 'none'] as ApprovalStatus[]).filter((s) => s !== 'approved' || canApprove).map((s) => ({ label: APPROVAL_TONE[s].label, icon: <span className={`inline-block h-2.5 w-2.5 rounded-full ${APPROVAL_TONE[s].dot}`} />, onSelect: () => run('approval', sel, { status: s }) })),
        ]
      : []),
    ...(canManage
      ? [
          { kind: 'separator' as const },
          ...(boardId ? [{ label: 'Remove from this board', onSelect: () => run('remove-from-board', sel) }] : []),
          ...tidyItems, // release1/B
          { label: n > 1 ? `Delete ${n}` : 'Delete', danger: true, onSelect: () => run('delete', sel) },
        ]
      : []),
  ];
  return <Menu anchor={at} items={items} onClose={onClose} />;
}

// --- release1/B ---
/** Archive/unarchive for the selection (S7a) and pin/unpin for a single card (S1). Rendered inside the MANAGE_BOARD block. */
function useTidyMenuItems(sel: string[], card: AssetCard, run: (kind: AssetActionKind, ids: string[]) => void): MenuItem[] {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const isMember = useSessionStore((s) => s.principal.kind === 'member');
  const pins = usePins(isMember ? wsId : undefined);
  const archived = sel.length === 1 ? !!card.archived_at : false;
  return [
    ...(sel.length === 1 && isMember ? [{ label: isPinned(pins.data, 'asset', card.id) ? 'Unpin from sidebar' : 'Pin to sidebar', onSelect: () => run('pin', [card.id]) }] : []),
    { label: archived ? 'Unarchive' : sel.length > 1 ? `Archive ${sel.length}` : 'Archive', onSelect: () => run(archived ? 'unarchive' : 'archive', sel) },
  ];
}

// --- release3/C ---
/** Download entries: a single image lists the workspace presets and "Custom export…"; several assets zip (originals). */
function useDownloadItems(sel: string[], card: AssetCard, run: (kind: AssetActionKind, ids: string[], opts?: AssetActionOpts) => void, canDownload: boolean): MenuItem[] {
  const wsId = useSessionStore((s) => s.workspace?.id);
  const isMember = useSessionStore((s) => s.principal.kind === 'member');
  const single = sel.length === 1 && card.type === 'image';
  const presets = useDownloadPresets(canDownload && isMember && single ? wsId : undefined);
  if (!canDownload) return [];
  if (sel.length > 1) return [{ label: `Download ${sel.length} as zip`, onSelect: () => run('download', sel, { card }) }];
  return downloadMenuItems({
    target: { versionId: card.active_version_id, assetId: card.id, filename: card.title, isImage: card.type === 'image' },
    presets: presets.data ?? [],
    onOriginal: () => run('download', sel, { card }),
    onPreset: (p) => run('download', sel, { card, presetId: p.id }),
    onCustom: isMember ? () => run('export', sel, { card }) : undefined,
  });
}
